import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { usePendingScope } from "@/lib/pending-edits";
import { LocalizedError } from "@/lib/localized-error";
import { COMPANY_SCOPE_COMPETENCY } from "./pending-scopes";
import { useCompanyCompetencyDraft } from "./use-company-competency-draft";
import {
  MAX_COMPETENCY_COUNT,
  MAX_COMPETENCY_LABEL_LENGTH,
  type UseCompanyAdminResult,
} from "./use-company-admin";

// The company's expertise — the match-relevant core (the company capability model
// §3). "Reaches the solver" is what that used to mean and no longer does: company matching is
// deferred (concept v3) and `CompanyCapabilityProjection` is deleted rather than parked (v4), so
// today this is what the company page shows and what company SEARCH indexes (concept 0005 §2).
//
// EXPERTISE IS PER-COMPANY FREE TEXT (@external-adr GENESIS-0031-company-expertise-is-per-company-free-text). There is no platform-wide vocabulary any
// more: no `competency_field` table, no picker over it, no "not in the list?" box, and no
// reference-data screen behind them. A company admin names an expertise and gives it a level; the
// label belongs to this company and the same word at another company is a different row that means
// nothing in common.
//
// THE SCREEN IS THE PERSON-PROFILE LADDER, not a form (concept v13, the company capability model). Three
// verbs are on it — "+" on a rung adds, a chip's panel renames or deletes it, dragging a chip
// re-levels — while the genesis INTERFACE behind them still has only two, add and remove (genesis
// @adr SPARK-0031-adrs-decide-ui-conventions-records@1 §4). Every rename and every re-level is
// therefore sent as a remove and an add, which `use-company-competency-draft` does and nothing above
// it needs to know.
//
// WHAT WENT WITH THE CATALOGUE, AND WHY IT IS NOT A REGRESSION TO WATCH FOR. Concept v3 §3 wanted
// ONE vocabulary so company vectors stayed comparable, and this screen used to carry the duplicate
// damping that made a shared list survivable — near-match suggestions, "use the existing field",
// a create that genesis could refuse as a clash. All of it is deleted. Ten companies can now each
// write "Regulatory Affairs", "Regulatory affairs" and "RegAffairs"; nothing merges them and
// nothing suggests. That was decided with the cost spelled out and is recorded in @external-adr GENESIS-0031-company-expertise-is-per-company-free-text decision's
// Consequences, so it is not a bug report waiting to be filed against this file.
//
// TWO RULES THIS FILE STILL EXISTS TO KEEP.
//
// 1. REMOVING IS NOT SETTING THE DEGREE TO 0. An absent row means NOT LISTED; a stored 0 means
//    listed and assessed as none. genesis keeps the two apart as a rule over the stored value.
//    The CANDIDATE PROJECTION this used to cite (@external-adr GENESIS-0021-company-competency-degree-stored-as-percent §5) is not the reason any more: concept v4
//    deleted the company vector space, so a company's expertise is projected nowhere and cannot rank
//    as "actively incapable" — see the company matching model decision's v4 note, "es gibt ab v4 überhaupt keinen
//    Firmen-Vektorraum, auch keinen eigenen". The rule stands on the simpler ground that a claim
//    nobody made must not be recorded as a measurement. So removal is its own explicit action calling
//    removeCompanyCompetency, and no path here ever writes a 0 to mean "gone".
//
// 2. THERE IS ONE SCALE. The degree is stored as a 0..100 percent and the seven levels are a lens
//    over it (@external-adr GENESIS-0021-company-competency-degree-stored-as-percent §1-2), so every value written here comes from the person-side
//    toStoragePercent/levelFromPercent in lib/skill-levels. No second scale, no nine steps, no
//    company-only names.
//
// A LISTED ROW DOES HAVE A LEVEL CONTROL NOW, and the rule that once forbade it is untouched.
// `setCompanyCompetency` still ADDS — a label the company already lists is refused with
// GENESIS001247 rather than re-scored — so changing a degree is still Remove then Add. What changed
// is where that lives: it is one STAGED intent here (`stageEdit`) and two requests in the draft's
// apply(), so dragging a chip to another rung is an affordance that works rather than one whose
// every click failed. The admin expresses an intention; the draft knows what genesis needs.
//
// THE DUPLICATE IS genesis's TO REFUSE, NOT THIS FILE'S TO PREVENT. Add stays live for a label
// already listed, differing in case or not, and the refusal comes back as a catalogued code
// (GENESIS001247) that use-company-admin turns into a sentence. One authority: a local copy of the
// rule would have to fold case and whitespace exactly as PostgreSQL's `lower()` does over whatever
// the company already holds, and being nearly right would hide the real answer behind a guess.
//
// HOW MANY ENTRIES IS CAPPED, AND THAT IS NOT A SECOND OPINION ABOUT FREE TEXT. genesis folds every
// label a company lists into that company's ONE global_search tsvector, and PostgreSQL refuses a
// tsvector past 1 MB — so an unbounded list lets a company reach a state where it cannot be
// reindexed, which then aborts a PLATFORM admin's industry rename for every other company in that
// industry. genesis caps it at MAX_COMPETENCY_COUNT and answers GENESIS001249; this file holds a copy
// so the refusal is given HERE rather than by the server. Note the difference from
// CompanyMemberEditor, which disables the contact-person control: the "+" on a rung stays live and
// the panel still opens, and the cap is answered when the panel is confirmed (`confirmPanelAdd`) —
// a ladder has a "+" on every rung, and greying out seven of them says less than one sentence does.
// The note is neutral text, not a warning: it is the rule, not a failure.
//
// NOTHING HERE WRITES ON THE CLICK ANY MORE. Adding and removing are STAGED — they change the list
// on screen and the SaveBar's entry, and they reach genesis when Save is pressed
// (use-company-competency-draft). Until then the whole edit view behaves as one preview, which is
// what Kevin asked for: curate the list, then commit it, rather than a stream of writes each of
// which is already applied.
//
// THE INLINE TWO-STEP ON REMOVE IS GONE WITH IT, and that is the point rather than an oversight.
// @adr SPARK-0011-destructive-actions-require-confirmation@1 asks for a confirmation before something destructive; a staged removal destroys nothing —
// it is visible on the row, reversible with Undo beside it, and dropped entirely by leaving edit
// mode. The confirmation that remains is Save.
//
// THE LADDER IS THE EDITOR, and it is the same one a person's skills use (@adr SPARK-0039-contact-persons-come-before-the-roster@2 postscript).
// There is no separate "Add an expertise" form under the list any more: the "+" on a rung adds AT
// that level, dragging a chip moves it, and clicking one opens rename and remove. Two
// representations of one list — a flat editor above, a ladder below — is exactly the drift this
// page has been spending its day undoing.
//
// ONE CONSEQUENCE, INHERITED DELIBERATELY. The diagram's inline editor holds a half-typed new entry
// in its own transient state, not in the draft, so it does not reach the SaveBar and is lost by
// navigating away. The old form's label and level DID report dirty. This is the person page's
// behaviour, unchanged, and matching it is the point; a company-only exception would be a second
// rule to keep.
interface CompetencyRow {
  label: string;
}

interface CompanyCompetencyEditorProps {
  competencies: readonly CompetencyRow[];
  admin: UseCompanyAdminResult;
  /**
   * True on the create state of the page (@adr SPARK-0038-creating-a-company-is-the-company-page-before-it-exists@2). The draft works exactly as it does on a stored
   * company — it stages and `apply()` writes — but it must not announce itself as unsaved work,
   * because the create state has its own single commit and no SaveBar.
   */
  creating?: boolean;
}

export function CompanyCompetencyEditor({
  competencies,
  admin,
  creating = false,
}: CompanyCompetencyEditorProps) {
  const intl = useIntl();
  const [message, setMessage] = useState<string | null>(null);
  // The half-typed new label. Transient on purpose and NOT in the draft, exactly as the ladder's
  // inline editor was: it does not reach the SaveBar and is lost by navigating away.
  const [pending, setPending] = useState("");
  const draft = useCompanyCompetencyDraft(competencies, admin);

  const listFull = draft.listFull;

  // What the ladder draws: the list as it will be once saved, minus what is on its way out. A row
  // staged for removal is not shown ON a rung — it would be indistinguishable from a live one — so
  // it is named underneath instead, where the Undo that takes it back also lives.
  const live = draft.projected.filter((entry) => !entry.isRemoved);
  const staged = draft.projected.filter((entry) => entry.isRemoved || entry.isNew || entry.isEdited);

  const label = intl.formatMessage({
    id: "company.admin.competencies.label",
    defaultMessage: "Expertise",
  });

  /** genesis's duplicate refusal (GENESIS001247), in this page's words. */
  function duplicateMessage(label: string): string {
    return intl.formatMessage(
      {
        id: "company.admin.competencies.duplicate",
        defaultMessage: "This company already lists the expertise ‘{label}’.",
      },
      { label },
    );
  }

  /** genesis's cap refusal (GENESIS001249), stated as the rule rather than as a failure. */
  function listFullMessage(): string {
    return intl.formatMessage(
      {
        id: "company.admin.competencies.listFull",
        defaultMessage:
          "This company already lists {max, number} expertise entries, which is the maximum. Remove one to add another.",
      },
      { max: MAX_COMPETENCY_COUNT },
    );
  }

  /**
   * Refuses what genesis would refuse, before the round trip, over the STAGED state rather than over
   * one form's contents — because there is no form any more and several entries can be wrong at
   * once. Each rule is genesis's, held here only so the answer arrives before the request.
   */
  function refusal(): string | null {
    const blank = live.find((entry) => entry.label.trim() === "");
    if (blank) {
      return intl.formatMessage({
        id: "company.admin.competencies.blank",
        defaultMessage: "An expertise needs a name. Give it one, or remove it.",
      });
    }
    const tooLong = live.find((entry) => entry.label.trim().length > MAX_COMPETENCY_LABEL_LENGTH);
    if (tooLong) {
      return intl.formatMessage(
        {
          id: "company.admin.competencies.tooLong",
          defaultMessage: "An expertise can be at most {max, number} characters long.",
        },
        { max: MAX_COMPETENCY_LABEL_LENGTH },
      );
    }
    const seen = new Set<string>();
    const duplicate = live.find((entry) => {
      const folded = entry.label.trim().toLowerCase();
      if (seen.has(folded)) return true;
      seen.add(folded);
      return false;
    });
    if (duplicate) return duplicateMessage(duplicate.label.trim());
    if (live.length > MAX_COMPETENCY_COUNT) return listFullMessage();
    return null;
  }

  async function saveOrThrow(): Promise<void> {
    const refused = refusal();
    if (refused) {
      setMessage(refused);
      throw new LocalizedError(refused);
    }
    const failures = await draft.apply();
    setMessage(failures[0] ?? null);
    if (failures.length > 0) throw new LocalizedError(failures[0]!);
  }

  function discardDraft() {
    setMessage(null);
    draft.discard();
  }

  usePendingScope(COMPANY_SCOPE_COMPETENCY, {
    // Silent while the company is still hypothetical — see CompanyEditingScopes.
    dirty: !creating && draft.dirty,
    save: saveOrThrow,
    discard: discardDraft,
  });

  return (
    <div className="flex flex-col gap-4">
      {/* A FLAT LIST, since concept v17 (0004 point 3, Kevin with Janek 2026-09-09). Until then this
          was the person page's seven-rung ladder (`SkillsDiagram`): the rung a chip sat on WAS the
          degree, and adding meant pressing "+" on a rung. Company expertise has no degree any more,
          so a ladder would be seven rows of one meaning — the surface says what a row now says, and
          nothing more.

          The three verbs stay the three verbs (v13): add, rename, remove. Renaming is still a remove
          plus an add against genesis's two-verb interface; the draft layer performs that, which is
          why an edit here is one `stageEdit` and not two gestures. */}
      <div className="flex flex-col gap-3">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            // THE ORDER OF THE TWO REFUSALS IS PART OF THE ANSWER, and it lives in `confirmAdd`
            // rather than here: a label that is both a duplicate AND past the cap gets the
            // duplicate message, because that is the one the admin can act on. A test pins it.
            const typed = pending.trim();
            if (typed === "") return;
            confirmAdd(typed, {
              taken: draft.hasLabel,
              full: listFull,
              say: setMessage,
              duplicate: duplicateMessage,
              listFullText: listFullMessage,
              stage: (staged) => {
                draft.stageAdd(staged);
                setPending("");
              },
            });
          }}
        >
          {/* NO VISIBLE LABEL. The section heading above already reads "Expertise"; a second copy
              inside the box says the same word twice and makes "the Expertise section" ambiguous to
              anything looking the heading up. The accessible name is on the input instead. */}
          <label className="flex flex-col gap-1">
            {/* NO `maxLength`. An over-length label is REFUSED with the rule quoted back
                (genesis GENESIS001229, mirrored by `refusal()` so the answer arrives before the
                request); a cap on the field would silently truncate what somebody pasted, which is
                a change to their text disguised as a limit. */}
            <input
              id="company-competency-input"
              value={pending}
              onChange={(event) => setPending(event.target.value)}
              aria-label={label}
              placeholder={intl.formatMessage({
                id: "company.admin.competencies.labelPlaceholder",
                defaultMessage: "e.g. Regulatory Affairs",
              })}
              className="min-w-[16rem] rounded-md bg-page px-3 py-2 text-smycles-meta outline-none ring-1 ring-ink/10 transition focus:ring-ink-accent-ui"
            />
          </label>
          {/* NOT disabled when the list is full: the click has to ANSWER. A disabled button says
              "no" without saying why, and the cap refusal (GENESIS001249) is exactly the sentence
              the admin needs — `confirmAdd` gives it. Only an empty field disables. */}
          <Button id="company-competency-add" type="submit" variant="secondary" disabled={pending.trim() === ""}>
            <FormattedMessage id="common.add" defaultMessage="Add" />
          </Button>
        </form>

        {live.length === 0 ? (
          <p id="company-competency-empty" className="smycles-field-meta text-ink-muted">
            <FormattedMessage
              id="company.admin.competencies.none"
              defaultMessage="No expertise listed yet."
            />
          </p>
        ) : (
          <ul id="company-competency-list" className="flex flex-wrap gap-2">
            {live.map((entry, i) => (
              <li key={entry.original} id={`company-competencyrow-${i}`} className="flex items-center gap-1 rounded-full bg-ink/[0.04] pl-3 pr-1 py-1">
                {/* RENAME IN PLACE, because the label is now the whole row: an input rather than a
                    panel, since there is no second field to ask for. */}
                <input
                  id={`company-competencyrow-${i}-name`}
                  value={entry.label}
                  onChange={(event) => draft.stageEdit(entry.original, { label: event.target.value })}
                  aria-label={intl.formatMessage({
                    id: "company.admin.competencies.editLabel",
                    defaultMessage: "Edit expertise",
                  })}
                  size={Math.max(entry.label.length, 4)}
                  className="bg-transparent text-smycles-meta outline-none"
                />
                <button
                  id={`company-competencyrow-${i}-remove`}
                  type="button"
                  // A STAGED ADDITION IS DROPPED, NOT MARKED FOR REMOVAL: staging a removal for a
                  // row genesis never had would ask it to delete something that does not exist. The
                  // ladder made the same distinction by offering Cancel instead of Delete.
                  onClick={() =>
                    entry.isNew ? draft.unstage(entry.original) : draft.stageRemove(entry.original)
                  }
                  title={intl.formatMessage({ id: "common.remove", defaultMessage: "Remove" })}
                  aria-label={intl.formatMessage({ id: "common.remove", defaultMessage: "Remove" })}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full text-ink-muted transition hover:bg-ink/[0.06] hover:text-ink"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* WHAT SAVE WILL DO, and the only route back for a removal — a chip that is gone from the
          ladder cannot carry its own Undo. Neutral and textual, like every other staged state on
          this page: it is a statement about what happens next, not a warning. */}
      {staged.length > 0 ? (
        <ul
          id="company-competency-pendinglist"
          aria-label={intl.formatMessage({
            id: "company.admin.competencies.stagedList",
            defaultMessage: "Pending expertise changes",
          })}
          className="flex flex-col gap-1 border-t border-ink/5 pt-3"
        >
          {staged.map((entry, i) => (
            <li key={entry.original} id={`company-competencypending-${i}`} className="flex flex-wrap items-baseline gap-2">
              <span id={`company-competencypending-${i}-text`} className="smycles-field-meta text-ink-muted">
                {entry.isRemoved ? (
                  <FormattedMessage
                    id="company.admin.competencies.pendingRemoveNamed"
                    defaultMessage="‘{label}’ will be removed when you save."
                    values={{ label: entry.original }}
                  />
                ) : entry.isNew ? (
                  <FormattedMessage
                    id="company.admin.competencies.pendingAddNamed"
                    defaultMessage="‘{label}’ will be added when you save."
                    values={{ label: entry.label }}
                  />
                ) : (
                  <FormattedMessage
                    id="company.admin.competencies.pendingEditNamed"
                    defaultMessage="‘{label}’ will be rewritten when you save."
                    values={{ label: entry.original }}
                  />
                )}
              </span>
              <Button id={`company-competencypending-${i}-undo`} variant="secondary" size="sm" onClick={() => draft.unstage(entry.original)}>
                <FormattedMessage id="common.undo" defaultMessage="Undo" />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Stated once, above nothing in particular, rather than per row: the cap is a fact about the
          whole list. Neutral text — it is the rule, not a failure. */}
      {listFull ? (
        <p id="company-competency-listfull" className="smycles-field-meta text-ink-muted">
          <FormattedMessage
            id="company.admin.competencies.listFull"
            defaultMessage="This company already lists {max, number} expertise entries, which is the maximum. Remove one to add another."
            values={{ max: MAX_COMPETENCY_COUNT }}
          />
        </p>
      ) : null}

      {message ? <p id="company-competency-message" className="smycles-field-meta text-ink">{message}</p> : null}
    </div>
  );
}

/**
 * What happens when the panel confirms an add.
 *
 * <p>THE TWO REFUSALS GENESIS WOULD GIVE, GIVEN HERE FIRST. `stageAdd` answers a duplicate by doing
 * nothing — silently, which is the one thing an admin must not meet — and the cap is genesis's
 * GENESIS001249. Both are answered before anything is staged, so the admin is told the rule rather
 * than watching a click do nothing or waiting for a round trip to refuse them.
 *
 * <p>A BLANK NAME IS IGNORED, NOT REFUSED. Nothing was typed, so there is nothing to explain, and
 * the message already on screen stays: it may be the refusal that is the reason the panel is still
 * open. The panel's own confirm is disabled while its one required field is empty, so this is
 * defence at an untyped boundary rather than a reachable UI path — which is precisely why it lives
 * in a function a test can call directly instead of inside a JSX closure nothing can reach.
 */
export function confirmAdd(
  label: string,
  on: {
    taken: (label: string) => boolean;
    full: boolean;
    say: (message: string | null) => void;
    duplicate: (label: string) => string;
    listFullText: () => string;
    stage: (label: string) => void;
  },
): void {
  const typed = label.trim();
  if (!typed) return;
  if (on.taken(typed)) {
    on.say(on.duplicate(typed));
    return;
  }
  if (on.full) {
    on.say(on.listFullText());
    return;
  }
  on.say(null);
  on.stage(typed);
}
